import { httpTokenCommon } from "../helper/http-common";
import { emitPaymentsChanged } from "../services/paymentsEvents";
import {
  ActivePaymentSession,
  clearActivePaymentSession,
  clearOrderPaymentAbandoned,
  markOrderPaymentAbandoned,
} from "./abandonedOrderPayments";

const HYPERPAY_SUCCESS =
  /^(000\.000\.|000\.100\.1|000\.[36]|000\.400\.[1][12]0)/;

/** How long after pressing Pay a page unload is treated as the gateway redirect (HyperPay checkouts live 30 min). */
const GATEWAY_SUBMIT_WINDOW_MS = 30 * 60 * 1000;

export type CancelGatewayOutcome =
  | "canceled"
  | "completed"
  | "still_processing"
  | "failed";

const GATEWAY_TYPES = new Set<ActivePaymentSession["type"]>([
  "CardPayment",
  "QiCard",
  "ZainCash",
]);

/**
 * Asks the payments API to release an in-flight card, Qi Card, or ZainCash
 * transaction. The server checks the gateway first: money already taken is
 * booked as paid and is not cancelled.
 */
export async function cancelGatewayPayment(
  transactionId: string
): Promise<CancelGatewayOutcome> {
  const id = transactionId.trim();
  if (!id) return "failed";

  try {
    const response = await httpTokenCommon.post(
      `/payments/cancel/${encodeURIComponent(id)}`,
      {},
      { silent: true }
    );
    const canceled = Boolean(
      (response.data as { canceled?: boolean } | undefined)?.canceled
    );
    return canceled ? "canceled" : "still_processing";
  } catch (err) {
    const axiosErr = err as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = axiosErr.response?.status;
    const code = String(axiosErr.response?.data?.error ?? "");
    if (status === 400 && (code === "PAYMENT_COMPLETED" || code.toLowerCase().includes("already completed"))) {
      return "completed";
    }
    if (status === 409) return "still_processing";
    return "failed";
  }
}

function fireKeepalive(
  method: "GET" | "POST",
  path: string
): void {
  const baseUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  if (!baseUrl || !token || !path) return;

  const url = `${baseUrl.replace(/\/$/, "")}${path}`;
  void fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(method === "POST" ? { "Content-Type": "application/json" } : {}),
    },
    body: method === "POST" ? "{}" : undefined,
    keepalive: true,
  }).catch(() => {});
}

function fireKeepaliveForSession(session: ActivePaymentSession): void {
  const txId = session.transactionId?.trim();
  if (!txId || session.type !== "CardPayment") return;

  fireKeepalive("POST", `/payments/cancel/${encodeURIComponent(txId)}`);
}

/**
 * Cancel the open gateway payment without abandoning the sales order.
 * Checkout drafts stay reusable so cancel does not mint a new order number.
 */
export async function releaseCheckoutGatewayPayment(
  session: Pick<ActivePaymentSession, "type" | "transactionId">
): Promise<CancelGatewayOutcome> {
  const txId = session.transactionId?.trim();
  if (!txId || !GATEWAY_TYPES.has(session.type)) return "failed";

  const outcome = await cancelGatewayPayment(txId);
  if (outcome === "canceled") emitPaymentsChanged();
  return outcome;
}

/**
 * User closed payment UI without finishing. Releases the in-flight gateway
 * payment so the order can be paid with another method.
 */
export async function handlePaymentClose(
  session: ActivePaymentSession
): Promise<CancelGatewayOutcome> {
  if (!session.orderId?.trim() || session.completed) return "failed";
  if (!session.transactionId?.trim()) return "failed";

  const outcome = await releaseCheckoutGatewayPayment(session);

  if (outcome === "completed") {
    clearOrderPaymentAbandoned(session.orderId);
    clearActivePaymentSession();
    emitPaymentsChanged();
    return outcome;
  }

  if (outcome !== "still_processing") {
    markOrderPaymentAbandoned(session.orderId);
    clearActivePaymentSession();
  }

  emitPaymentsChanged();
  return outcome;
}

/**
 * pagehide fires for tab close AND for leaving the origin (QiCard / ZainCash hosted
 * checkout). Cancelling QiCard here is what broke localhost: the Pay request then
 * gets QiCard error 22 ("Payment not in correct status") because we already cancelled.
 */
export function handlePaymentTabClose(session: ActivePaymentSession): void {
  if (!session.orderId?.trim() || session.completed) return;
  if (session.type === "QiCard" || session.type === "ZainCash") {
    return;
  }
  // The card form was just submitted: this pagehide is the redirect to HyperPay / 3-D Secure.
  // Cancelling here killed every web card payment mid-flight ("FAILED: Canceled" →
  // "No payment session found" → "card payment session expired").
  if (
    session.gatewaySubmittedAt &&
    Date.now() - session.gatewaySubmittedAt < GATEWAY_SUBMIT_WINDOW_MS
  ) {
    return;
  }
  markOrderPaymentAbandoned(session.orderId);
  fireKeepaliveForSession(session);
}

export function isHyperPaySuccessCode(code?: string): boolean {
  return !!code && HYPERPAY_SUCCESS.test(code);
}
